/* Markus Upgraded (draft), Option B: foundation JS.
   Vanilla, no dependencies, ES2018, deferred.
   Chrome: header compact + --up-header-h, announcement rotation, nav dropdowns (touch and
   keyboard), drawers (menu, cart) with focus trap and scroll lock, search overlay with
   predictive results, cart API (window.CloneCart, name kept from the clone for continuity),
   toast (window.UpToast), modal (window.UpModal), quantity steppers, quick add on cards,
   product form add-to-cart, sticky add-to-cart bar, newsletter inline validation.
   Product: window.UpVariantPicker (blueprint U-E4), one instance per [data-up-picker] root,
   emits "up:variant" on the root. Blueprint: site_clone_2026-08-13/upgrade/blueprint_upgrade.md */
(function () {
  'use strict';

  var T = window.UpTheme || {};
  var S = T.settings || {};
  var R = T.routes || {};
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ utils */
  function ready(fn) { if (document.readyState !== 'loading') { fn(); } else { document.addEventListener('DOMContentLoaded', fn); } }
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function escapeHtml(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
  function setBool(v, dflt) { return (v === null || v === undefined) ? dflt : !!v; }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    cents = parseInt(cents, 10) || 0;
    var fmt = format || T.moneyFormat || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    function withDelims(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision; thousands = thousands || ','; decimal = decimal || '.';
      if (isNaN(number) || number == null) { return '0'; }
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    }
    var m = fmt.match(placeholderRegex);
    var key = m ? m[1] : 'amount';
    var value;
    switch (key) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelims(cents, 2, ' ', '.'); break;
      default: value = withDelims(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }
  window.UpMoney = { format: formatMoney };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function focusables(root) { return qsa(FOCUSABLE, root).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; }); }
  function trapTab(e, root) {
    if (e.key !== 'Tab') return;
    var f = focusables(root);
    if (!f.length) { e.preventDefault(); root.focus(); return; }
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === root)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  function announce(text, el) {
    var live = el || qs('[data-up-cart-live]');
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 30);
  }

  /* ------------------------------------------------------------------ header */
  function initHeader() {
    var header = qs('[data-up-header]');
    if (!header) return;
    var root = document.documentElement;
    /* --up-header-h is a measured OUTPUT (read by the sticky ATC, facets sidebar, gallery, cart
       summary and scroll offsets). The header's own row is sized by the CSS only token
       --up-header-inner-h, so writing the measurement here can never change the header's size
       and feed back into this observer. Write only on change so the ResizeObserver settles. */
    var lastH = null;
    function setVar() {
      var h = header.offsetHeight;
      if (!h || h === lastH) return;
      lastH = h;
      root.style.setProperty('--up-header-h', h + 'px');
    }
    /* compact with hysteresis: on above 80px, off again only below 48px, so the desktop
       12px shrink cannot flip flop around one threshold while the page reflows */
    var compact = false;
    function onScroll() {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      var next = compact ? y > 48 : y > 80;
      if (next !== compact) {
        compact = next;
        header.classList.toggle('is-compact', compact);
        setTimeout(setVar, 220);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debounce(setVar, 100));
    if (window.ResizeObserver) { new ResizeObserver(setVar).observe(header); }
    setVar(); onScroll();
  }

  /* ------------------------------------------------------------ announcement */
  function initAnnouncement() {
    var bar = qs('[data-up-announcement]');
    if (!bar) return;
    var key = 'up-announcement-dismissed';
    if (S.announcementDismissible && sessionStorage.getItem(key) === '1') { bar.classList.add('is-dismissed'); return; }
    var closeBtn = qs('[data-up-announcement-close]', bar);
    if (closeBtn) closeBtn.addEventListener('click', function () { bar.classList.add('is-dismissed'); try { sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ } });
    var msgs = qsa('[data-up-announcement-msg]', bar);
    if (msgs.length < 2 || reduceMotion) return;
    var interval = parseInt(bar.getAttribute('data-up-interval'), 10) || 6000;
    var i = 0, timer = null, paused = false;
    function tick() {
      if (paused) return;
      msgs[i].classList.remove('is-active');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-active');
    }
    function start() { stop(); timer = setInterval(tick, interval); }
    function stop() { if (timer) clearInterval(timer); timer = null; }
    bar.addEventListener('mouseenter', function () { paused = true; });
    bar.addEventListener('mouseleave', function () { paused = false; });
    bar.addEventListener('focusin', function () { paused = true; });
    bar.addEventListener('focusout', function () { paused = false; });
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });
    start();
  }

  /* -------------------------------------------------------------- dropdowns */
  function initNav() {
    var nav = qs('[data-up-nav]');
    if (!nav) return;
    var items = qsa('[data-up-nav-item]', nav);
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    function closeAll(except) {
      items.forEach(function (li) {
        if (li === except) return;
        li.classList.remove('is-open');
        var a = qs('[data-up-nav-parent]', li); if (a) a.setAttribute('aria-expanded', 'false');
      });
    }
    items.forEach(function (li) {
      var parent = qs('[data-up-nav-parent]', li);
      if (!parent) return;
      parent.addEventListener('click', function (e) {
        var open = li.classList.contains('is-open');
        if (coarse || e.detail === 0) {
          /* touch: first tap opens, second follows; keyboard Enter: toggle */
          if (!open) { e.preventDefault(); closeAll(li); li.classList.add('is-open'); parent.setAttribute('aria-expanded', 'true'); }
        }
      });
      parent.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === ' ') {
          e.preventDefault(); closeAll(li); li.classList.add('is-open'); parent.setAttribute('aria-expanded', 'true');
          var first = qs('.up-dropdown-link', li); if (first) first.focus();
        }
      });
      li.addEventListener('keydown', function (e) {
        var links = qsa('.up-dropdown-link', li);
        var idx = links.indexOf(document.activeElement);
        if (e.key === 'Escape') { closeAll(); parent.focus(); }
        else if (e.key === 'ArrowDown' && idx > -1) { e.preventDefault(); (links[idx + 1] || links[0]).focus(); }
        else if (e.key === 'ArrowUp' && idx > -1) { e.preventDefault(); (links[idx - 1] || links[links.length - 1]).focus(); }
      });
      li.addEventListener('focusout', function (e) { if (!li.contains(e.relatedTarget)) { li.classList.remove('is-open'); parent.setAttribute('aria-expanded', 'false'); } });
    });
    document.addEventListener('click', function (e) { if (!e.target.closest('[data-up-nav-item]')) closeAll(); });
  }

  /* ---------------------------------------------------------------- drawers */
  var Drawers = (function () {
    var overlay = null, current = null, lastFocus = null;
    function get(id) { return document.getElementById(id); }
    function lock(on) { document.body.classList.toggle('up-locked', on); }
    function open(id, opener) {
      var d = get(id);
      if (!d) return;
      if (current && current !== d) close(true);
      overlay = overlay || qs('[data-up-overlay]');
      lastFocus = opener || document.activeElement;
      d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false');
      if (overlay) { overlay.hidden = false; requestAnimationFrame(function () { overlay.classList.add('is-visible'); }); }
      lock(true);
      current = d;
      qsa('[data-up-drawer-open="' + id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      setTimeout(function () { var f = focusables(d); (f[0] || d).focus(); }, 60);
      document.dispatchEvent(new CustomEvent('up:drawer:open', { detail: { id: id } }));
    }
    function close(silent) {
      if (!current) return;
      var d = current;
      d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true');
      qsa('[data-up-drawer-open="' + d.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      current = null;
      if (!silent) {
        if (overlay) { overlay.classList.remove('is-visible'); setTimeout(function () { if (!current) overlay.hidden = true; }, 320); }
        lock(false);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      document.dispatchEvent(new CustomEvent('up:drawer:close', { detail: { id: d.id } }));
    }
    function init() {
      overlay = qs('[data-up-overlay]');
      document.addEventListener('click', function (e) {
        var openBtn = e.target.closest('[data-up-drawer-open]');
        if (openBtn) { e.preventDefault(); var id = openBtn.getAttribute('data-up-drawer-open'); if (id === 'up-drawer-cart' && window.CloneCart) window.CloneCart.refresh(); open(id, openBtn); return; }
        if (e.target.closest('[data-up-drawer-close]') || e.target.closest('[data-up-overlay]')) { e.preventDefault(); close(); return; }
        var acc = e.target.closest('[data-up-acc-toggle]');
        if (acc) {
          var panel = acc.closest('li') ? qs('[data-up-acc-panel]', acc.closest('li')) : null;
          var expanded = acc.getAttribute('aria-expanded') === 'true';
          acc.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          if (panel) panel.hidden = expanded;
        }
      });
      document.addEventListener('keydown', function (e) {
        if (!current) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        trapTab(e, current);
      });
    }
    return { open: open, close: close, init: init, isOpen: function () { return !!current; }, current: function () { return current; } };
  })();
  window.UpDrawers = Drawers;

  /* ------------------------------------------------------------------ toast */
  window.UpToast = {
    show: function (text, opts) {
      opts = opts || {};
      var host = qs('[data-up-toasts]');
      if (!host) return;
      while (host.children.length >= 2) host.removeChild(host.firstChild);
      var el = document.createElement('div');
      el.className = 'up-toast' + (opts.type === 'error' ? ' up-toast--error' : '');
      el.setAttribute('role', 'status');
      el.innerHTML = '<span>' + escapeHtml(text) + '</span>' + (opts.link ? '<a href="' + escapeHtml(opts.link) + '">' + escapeHtml(opts.label || 'View') + '</a>' : '');
      host.appendChild(el);
      requestAnimationFrame(function () { el.classList.add('is-visible'); });
      setTimeout(function () { el.classList.remove('is-visible'); setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 250); }, opts.timeout || 3200);
    }
  };

  /* ------------------------------------------------------------------ modal */
  window.UpModal = (function () {
    var underlay, panel, titleEl, bodyEl, lastFocus;
    function els() { underlay = underlay || qs('[data-up-modal]'); if (!underlay) return false; panel = qs('.up-modal', underlay); titleEl = qs('[data-up-modal-title]', underlay); bodyEl = qs('[data-up-modal-body]', underlay); return true; }
    function open(opts) {
      if (!els()) return;
      opts = opts || {};
      lastFocus = document.activeElement;
      titleEl.textContent = opts.title || '';
      bodyEl.innerHTML = opts.html || '';
      panel.classList.toggle('up-modal--borderless', !!opts.borderless);
      underlay.hidden = false;
      document.body.classList.add('up-locked');
      setTimeout(function () { var f = focusables(panel); (f[0] || panel).focus(); }, 30);
      document.dispatchEvent(new CustomEvent('up:modal:open'));
    }
    function close() {
      if (!els() || underlay.hidden) return;
      underlay.hidden = true;
      if (!Drawers.isOpen()) document.body.classList.remove('up-locked');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      document.dispatchEvent(new CustomEvent('up:modal:close'));
    }
    function init() {
      if (!els()) return;
      underlay.addEventListener('click', function (e) { if (e.target === underlay || e.target.closest('[data-up-modal-close]')) close(); });
      document.addEventListener('keydown', function (e) { if (underlay.hidden) return; if (e.key === 'Escape') { close(); return; } trapTab(e, panel); });
    }
    return { open: open, close: close, init: init };
  })();

  /* --------------------------------------------------------------- cart API */
  function parseJson(r, fallbackMsg) {
    return r.text().then(function (txt) {
      var j = null;
      try { j = txt ? JSON.parse(txt) : null; } catch (e) { j = null; }
      if (!r.ok) { var err = new Error((j && (j.description || j.message)) || fallbackMsg + ' (HTTP ' + r.status + ')'); err.data = j; throw err; }
      return j;
    });
  }
  var CloneCart = window.CloneCart = window.CloneCart || {};
  CloneCart._cart = null;
  CloneCart.refresh = function () {
    return fetch((R.cart || '/cart') + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { CloneCart._render(cart); return cart; })
      .catch(function () { return null; });
  };
  CloneCart.add = function (payload) {
    var body, headers = { 'Accept': 'application/json' };
    if (payload instanceof FormData) { body = payload; }
    else { body = JSON.stringify(payload); headers['Content-Type'] = 'application/json'; }
    return fetch((R.cartAdd || '/cart/add') + '.js', { method: 'POST', credentials: 'same-origin', headers: headers, body: body })
      .then(function (r) { return parseJson(r, 'Could not add to cart'); })
      .then(function (item) { return CloneCart.refresh().then(function (cart) { document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: item, cart: cart } })); return item; }); });
  };
  CloneCart.updateLine = function (key, qty) {
    return fetch((R.cartChange || '/cart/change') + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) })
      .then(function (r) { return parseJson(r, 'Could not update cart'); })
      .then(function (cart) { CloneCart._render(cart); return cart; });
  };
  CloneCart.open = function () { CloneCart.refresh(); Drawers.open('up-drawer-cart'); };
  CloneCart.close = function () { Drawers.close(); };
  CloneCart._render = function (cart) {
    if (!cart) return;
    CloneCart._cart = cart;
    var count = cart.item_count;
    qsa('[data-up-cart-count]').forEach(function (el) { el.textContent = count; el.classList.toggle('is-empty', count === 0); el.classList.add('is-bump'); setTimeout(function () { el.classList.remove('is-bump'); }, 200); });
    qsa('[data-up-cart-count-label]').forEach(function (el) { el.textContent = '(' + count + ')'; });
    qsa('[data-up-cart-subtotal]').forEach(function (el) { el.textContent = formatMoney(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price); });
    var drawer = qs('[data-up-cart-drawer]');
    if (drawer) {
      var lines = qs('[data-up-cart-lines]', drawer), empty = qs('[data-up-cart-empty]', drawer), foot = qs('[data-up-cart-foot]', drawer);
      var has = count > 0;
      if (empty) empty.hidden = has;
      if (foot) foot.hidden = !has;
      if (lines) {
        lines.hidden = !has;
        lines.innerHTML = cart.items.map(function (it) {
          var img = it.featured_image && it.featured_image.url ? it.featured_image.url : (it.image || '');
          if (img) img = img.replace(/(\.[a-z]{3,4})(\?.*)?$/i, '_144x144$1');
          var variant = (it.variant_title && it.variant_title !== 'Default Title') ? '<p class="up-cart-line-variant">' + escapeHtml(it.variant_title) + '</p>' : '';
          /* 2026-10-28: visible line properties (Backordered: ...) show in the drawer like on the cart page */
          var props = '';
          if (it.properties) { Object.keys(it.properties).forEach(function (k) { var pv = it.properties[k]; if (k.charAt(0) === '_' || pv == null || pv === '') return; props += '<p class="up-cart-line-variant up-cart-line-prop">' + escapeHtml(k) + ': <strong>' + escapeHtml(pv) + '</strong></p>'; }); }
          variant += props;
          return '<li class="up-cart-line" data-up-line-key="' + escapeHtml(it.key) + '">' +
            '<a class="up-cart-line-media" href="' + escapeHtml(it.url) + '" tabindex="-1" aria-hidden="true">' + (img ? '<img src="' + escapeHtml(img) + '" alt="" width="72" height="72" loading="lazy">' : '') + '</a>' +
            '<div class="up-cart-line-info">' +
              '<a class="up-cart-line-title" href="' + escapeHtml(it.url) + '">' + escapeHtml(it.product_title) + '</a>' + variant +
              '<div class="up-cart-line-row">' +
                '<div class="up-qty up-qty--sm" data-up-qty>' +
                  '<button type="button" class="up-qty-btn" data-up-line-minus aria-label="Decrease quantity"' + (it.quantity <= 1 ? ' disabled' : '') + '><svg class="up-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><use href="#up-i-minus"></use></svg></button>' +
                  '<input class="up-qty-input" type="number" inputmode="numeric" min="1" value="' + it.quantity + '" data-up-line-qty aria-label="Quantity">' +
                  '<button type="button" class="up-qty-btn" data-up-line-plus aria-label="Increase quantity"><svg class="up-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><use href="#up-i-plus"></use></svg></button>' +
                '</div>' +
                '<span class="up-cart-line-price">' + formatMoney(it.final_line_price) + '</span>' +
              '</div>' +
              '<button type="button" class="up-cart-line-remove" data-up-line-remove>Remove</button>' +
            '</div></li>';
        }).join('');
      }
    }
    /* cart page rows update in place */
    qsa('[data-up-cart-page] [data-up-line-key]').forEach(function (row) {
      var key = row.getAttribute('data-up-line-key');
      var it = cart.items.filter(function (x) { return x.key === key; })[0];
      if (!it) { row.parentNode.removeChild(row); return; }
      var price = qs('[data-up-line-price]', row); if (price) price.textContent = formatMoney(it.final_line_price);
      var qty = qs('[data-up-line-qty]', row); if (qty && document.activeElement !== qty) qty.value = it.quantity;
      var minus = qs('[data-up-line-minus]', row); if (minus) minus.disabled = it.quantity <= 1;
      row.classList.remove('is-busy');
    });
    var page = qs('[data-up-cart-page]');
    if (page) { var pe = qs('[data-up-cart-empty]', page), pf = qs('[data-up-cart-page-full]', page); if (pe) pe.hidden = count > 0; if (pf) pf.hidden = count === 0; }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  };
  function initCartLines() {
    var pending = {};
    function change(row, qty) {
      var key = row.getAttribute('data-up-line-key');
      row.classList.add('is-busy');
      clearTimeout(pending[key]);
      pending[key] = setTimeout(function () {
        CloneCart.updateLine(key, qty).then(function () { announce(qty === 0 ? 'Item removed' : 'Cart updated'); }).catch(function (err) {
          row.classList.remove('is-busy');
          window.UpToast.show(err.message || 'Could not update cart', { type: 'error' });
          CloneCart.refresh();
        });
      }, 250);
    }
    document.addEventListener('click', function (e) {
      var row = e.target.closest('[data-up-line-key]');
      if (!row) return;
      var input = qs('[data-up-line-qty]', row);
      var q = input ? parseInt(input.value, 10) || 1 : 1;
      if (e.target.closest('[data-up-line-minus]')) { e.preventDefault(); if (q > 1) { input.value = q - 1; change(row, q - 1); } }
      else if (e.target.closest('[data-up-line-plus]')) { e.preventDefault(); input.value = q + 1; change(row, q + 1); }
      else if (e.target.closest('[data-up-line-remove]')) { e.preventDefault(); change(row, 0); }
    });
    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-up-line-qty]');
      if (!input) return;
      var row = input.closest('[data-up-line-key]');
      var q = Math.max(0, parseInt(input.value, 10) || 0);
      change(row, q);
    });
  }

  /* --------------------------------------------------- generic qty stepper */
  function initQtySteppers() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-up-qty-minus], [data-up-qty-plus]');
      if (!btn) return;
      var wrap = btn.closest('[data-up-qty]');
      var input = wrap ? qs('input', wrap) : null;
      if (!input) return;
      var min = parseInt(input.min, 10) || 1;
      var v = parseInt(input.value, 10) || min;
      v = btn.hasAttribute('data-up-qty-minus') ? Math.max(min, v - 1) : v + 1;
      input.value = v;
      var minus = qs('[data-up-qty-minus]', wrap); if (minus) minus.disabled = v <= min;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* --------------------------------------------------------- search overlay */
  function initSearch() {
    var overlay = qs('[data-up-search]');
    if (!overlay) return;
    var input = qs('[data-up-search-input]', overlay);
    var results = qs('[data-up-search-results]', overlay);
    var openBtns = qsa('[data-up-search-open]');
    var controller = null;
    function open() { overlay.hidden = false; openBtns.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); }); setTimeout(function () { input.focus(); }, 30); }
    function close() { overlay.hidden = true; openBtns.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); }); if (results) { results.hidden = true; results.innerHTML = ''; } }
    openBtns.forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); if (overlay.hidden) open(); else close(); }); });
    overlay.addEventListener('click', function (e) { if (e.target.closest('[data-up-search-close]')) { e.preventDefault(); close(); var b = openBtns[0]; if (b) b.focus(); } });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !overlay.hidden) { close(); var b = openBtns[0]; if (b) b.focus(); } });
    document.addEventListener('click', function (e) { if (!overlay.hidden && !e.target.closest('[data-up-search]') && !e.target.closest('[data-up-search-open]')) close(); });
    if (!results || !R.predictiveSearch) return;
    var run = debounce(function () {
      var q = input.value.trim();
      if (controller) controller.abort();
      if (q.length < 2) { results.hidden = true; results.innerHTML = ''; return; }
      controller = window.AbortController ? new AbortController() : null;
      var url = R.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last';
      fetch(url, { signal: controller ? controller.signal : undefined, headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var res = (data && data.resources && data.resources.results) || {};
          var html = '';
          if (res.products && res.products.length) {
            html += '<div class="up-search-group"><p class="up-search-group-title">Products</p>' + res.products.map(function (p) {
              var img = p.image ? '<img class="up-search-thumb" src="' + escapeHtml(p.image.replace(/(\.[a-z]{3,4})(\?.*)?$/i, '_96x96$1')) + '" alt="" width="48" height="48" loading="lazy">' : '<span class="up-search-thumb"></span>';
              var price = p.price ? '<span class="up-search-item-price">' + escapeHtml(formatMoney(Math.round(parseFloat(p.price) * 100))) + '</span>' : '';
              return '<a class="up-search-item" href="' + escapeHtml(p.url) + '">' + img + '<span class="up-search-item-title">' + escapeHtml(p.title) + '</span>' + price + '</a>';
            }).join('') + '</div>';
          }
          ['collections', 'pages'].forEach(function (k) {
            if (res[k] && res[k].length) {
              html += '<div class="up-search-group"><p class="up-search-group-title">' + (k === 'collections' ? 'Collections' : 'Pages') + '</p>' + res[k].map(function (c) {
                return '<a class="up-search-item" href="' + escapeHtml(c.url) + '"><span class="up-search-item-title">' + escapeHtml(c.title) + '</span></a>';
              }).join('') + '</div>';
            }
          });
          if (!html) { html = '<p class="up-search-empty">No results for "' + escapeHtml(q) + '"</p>'; }
          html += '<a class="up-link up-search-all" href="' + escapeHtml(R.search) + '?q=' + encodeURIComponent(q) + '">See all results for "' + escapeHtml(q) + '"</a>';
          results.innerHTML = html; results.hidden = false;
        })
        .catch(function (err) { if (err && err.name === 'AbortError') return; results.hidden = true; });
    }, 250);
    input.addEventListener('input', run);
  }

  /* --------------------------------------------------- newsletter validation */
  function initNewsletter() {
    qsa('.up-newsletter-form').forEach(function (form) {
      var email = qs('[data-up-newsletter-email]', form), hint = qs('[data-up-newsletter-hint]', form);
      if (!email) return;
      form.addEventListener('submit', function (e) {
        var ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim());
        if (!ok) { e.preventDefault(); email.classList.add('is-error'); if (hint) hint.hidden = false; email.focus(); }
      });
      email.addEventListener('input', function () { email.classList.remove('is-error'); if (hint) hint.hidden = true; });
    });
  }

  /* =========================================================================
     UpVariantPicker (blueprint U-E4)
     ========================================================================= */
  function UpVariantPicker(root, opts) {
    opts = opts || {};
    this.root = root;
    this.context = opts.context || root.getAttribute('data-up-picker-context') || 'pdp';
    this.prefix = root.getAttribute('data-up-picker-prefix') || 'up-picker';
    this.productId = root.getAttribute('data-up-product-id');
    this.productUrl = root.getAttribute('data-up-product-url') || '';
    var vEl = qs('[data-up-variants]', root), oEl = qs('[data-up-options]', root);
    try { this.variants = opts.variants || (vEl ? JSON.parse(vEl.textContent) : []); } catch (e) { this.variants = []; }
    try { this.options = opts.options || (oEl ? JSON.parse(oEl.textContent) : []); } catch (e) { this.options = []; }
    this.scope = root.closest('[data-up-product]') || document;
    this.live = qs('[data-up-live]', root);
    this.groups = qsa('[data-up-option]', root);
    this.listeners = { change: [] };
    this.showDelta = setBool(S.showSwatchPriceDelta, true);
    this.variant = null;
    var self = this;
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches && (t.matches('[data-up-swatch-input]') || t.matches('[data-up-opt-index]'))) {
        self._onChange(parseInt(t.getAttribute('data-up-opt-index'), 10), t.value);
      }
    });
    root.__upPicker = this;
    this._resolveFromInputs(true);
    this._syncStates();
    this._syncDom(true);
  }
  UpVariantPicker.prototype._selections = function () {
    var sel = [];
    this.groups.forEach(function (g) {
      var idx = parseInt(g.getAttribute('data-up-option'), 10);
      var checked = qs('input[type="radio"]:checked', g);
      var select = qs('select[data-up-opt-index]', g);
      sel[idx] = checked ? checked.value : (select ? select.value : null);
    });
    return sel;
  };
  UpVariantPicker.prototype._find = function (sel) {
    return this.variants.filter(function (v) {
      for (var i = 0; i < sel.length; i++) { if (sel[i] != null && v.options[i] !== sel[i]) return false; }
      return true;
    })[0] || null;
  };
  UpVariantPicker.prototype._resolveFromInputs = function (silent) {
    var sel = this._selections();
    var v = this._find(sel);
    if (!v && this.variants.length) { v = this.variants.filter(function (x) { return x.available; })[0] || this.variants[0]; this._setInputs(v); }
    this.variant = v;
    return v;
  };
  UpVariantPicker.prototype._setInputs = function (v) {
    var self = this;
    this.groups.forEach(function (g) {
      var idx = parseInt(g.getAttribute('data-up-option'), 10);
      var val = v.options[idx];
      var radio = qsa('input[type="radio"]', g).filter(function (r) { return r.value === val; })[0];
      if (radio) radio.checked = true;
      var select = qs('select[data-up-opt-index]', g); if (select) select.value = val;
    });
  };
  UpVariantPicker.prototype._onChange = function (changedIndex, changedValue) {
    var sel = this._selections();
    var v = this._find(sel);
    var switched = false;
    if (!v) {
      /* missing combination: first available variant carrying this value, else first with the value */
      var carrying = this.variants.filter(function (x) { return x.options[changedIndex] === changedValue; });
      v = carrying.filter(function (x) { return x.available; })[0] || carrying[0] || this.variant;
      if (v) { this._setInputs(v); switched = true; }
    }
    this.variant = v;
    this._syncStates();
    this._syncDom(false);
    if (switched) this._announce('Options updated');
    var self = this;
    this.listeners.change.forEach(function (fn) { fn(self.variant); });
    this.root.dispatchEvent(new CustomEvent('up:variant', { bubbles: true, detail: { variant: this.variant, product: this.productId, picker: this } }));
  };
  UpVariantPicker.prototype._syncStates = function () {
    var self = this, cur = this.variant, sel = this._selections();
    this.groups.forEach(function (g) {
      var gi = parseInt(g.getAttribute('data-up-option'), 10);
      qsa('[data-up-swatch]', g).forEach(function (label) {
        var val = label.getAttribute('data-up-value');
        var trial = sel.slice(); trial[gi] = val;
        var v = self._find(trial);
        label.classList.toggle('is-missing', !v);
        label.classList.toggle('is-soldout', !!v && !v.available);
        label.classList.toggle('is-selected', !!cur && cur.options[gi] === val);
        var deltaEl = qs('[data-up-delta]', label);
        if (deltaEl) {
          var delta = (v && cur && self.showDelta) ? (v.price - cur.price) : 0;
          if (delta !== 0 && v !== cur) { deltaEl.textContent = (delta > 0 ? '+' : '-') + formatMoney(Math.abs(delta)); deltaEl.hidden = false; label.classList.add('has-delta'); }
          else { deltaEl.textContent = ''; deltaEl.hidden = true; label.classList.remove('has-delta'); }
        }
      });
      var selEl = qs('[data-up-opt-selected]', g), helpEl = qs('[data-up-opt-help]', g);
      if (selEl && cur) selEl.textContent = mainText(cur.options[gi]);
      if (helpEl) { var so = cur && !cur.available; helpEl.textContent = so ? (geoLabelFor(self.scope, cur) || 'Sold out') : ''; helpEl.hidden = !so; }
    });
  };
  /* 2026-10-03: a variant that is unavailable only because it cannot ship to the visitor's
     country (Liquid lists those ids in data-up-geo-ids) is "Not available in <country>", never "Sold out". */
  /* 2026-10-28 (Heath at PakLink): a variant that sells when out of stock and sits at or below zero is
     backordered. Liquid lists those ids in data-up-backorder-ids on the product form (label in
     data-up-backorder-label). Returns the button label or null. */
  function backorderFor(scope, v) {
    if (!v || !v.available || !scope) return null;
    var el = (scope.hasAttribute && scope.hasAttribute('data-up-backorder-ids')) ? scope : qs('[data-up-backorder-ids]', scope);
    if (!el) return null;
    var ids = ',' + (el.getAttribute('data-up-backorder-ids') || '') + ',';
    return ids.indexOf(',' + v.id + ',') >= 0 ? (el.getAttribute('data-up-backorder-label') || 'Backorder now') : null;
  }
  function geoLabelFor(scope, v, btn) {
    if (!v || v.available) return null;
    var el = (btn && btn.hasAttribute && btn.hasAttribute('data-up-geo-ids')) ? btn : null;
    if (!el && scope) el = (scope.hasAttribute && scope.hasAttribute('data-up-geo-ids')) ? scope : qs('[data-up-geo-ids]', scope);
    if (!el) return null;
    var ids = ',' + (el.getAttribute('data-up-geo-ids') || '') + ',';
    return ids.indexOf(',' + v.id + ',') >= 0 ? (el.getAttribute('data-up-geo-label') || 'Not available in your country') : null;
  }
  function mainText(val) {
    if (!val) return '';
    var m = String(val).match(/^(.*?)\s*\((.*)\)\s*$/);
    return m ? m[1] : String(val);
  }
  UpVariantPicker.prototype._syncDom = function (initial) {
    var v = this.variant, scope = this.scope;
    if (!v) return;
    qsa('input[name="id"]', scope).forEach(function (i) { i.value = v.id; });
    var onSale = v.compare_at_price && v.compare_at_price > v.price;
    qsa('[data-up-price]', scope).forEach(function (el) { el.textContent = formatMoney(v.price); });
    qsa('[data-up-compare]', scope).forEach(function (el) { el.textContent = onSale ? formatMoney(v.compare_at_price) : ''; el.hidden = !onSale; });
    qsa('[data-up-savings]', scope).forEach(function (el) { el.hidden = !onSale; });
    qsa('[data-up-savings-amount]', scope).forEach(function (el) { el.textContent = onSale ? formatMoney(v.compare_at_price - v.price) : ''; });
    qsa('[data-up-sku]', scope).forEach(function (el) { el.textContent = v.sku || ''; var w = el.closest('[data-up-sku-wrap]'); if (w) w.hidden = !v.sku; });
    qsa('[data-up-selected-options]', scope).forEach(function (el) { el.textContent = (v.options || []).map(mainText).join(', '); });
    var atcLabel = S.atcLabel || 'Add to cart', soldLabel = S.soldOutLabel || 'Sold out';
    var geoLabel = geoLabelFor(scope, v);
    var boLabel = backorderFor(scope, v);
    qsa('[data-up-atc]', scope).forEach(function (btn) {
      btn.disabled = !v.available;
      var t = qs('[data-up-atc-text]', btn); if (t) t.textContent = v.available ? (boLabel || atcLabel) : (geoLabelFor(scope, v, btn) || soldLabel);
      btn.classList.remove('is-added');
    });
    qsa('[data-up-notify]', scope).forEach(function (el) { el.hidden = v.available || !!geoLabel; });
    qsa('[data-up-geo-note]', scope).forEach(function (el) { el.hidden = !geoLabel; });
    qsa('[data-up-backorder-note]', scope).forEach(function (el) { el.hidden = !boLabel; });
    qsa('[data-up-backorder-prop]', scope).forEach(function (el) { el.disabled = !boLabel; });
    /* gallery stage */
    var img = v.featured_image && v.featured_image.src ? v.featured_image.src : (v.featured_media && v.featured_media.preview_image ? v.featured_media.preview_image.src : null);
    if (img) {
      qsa('[data-up-stage-img]', scope).forEach(function (el) {
        var base = img.split('?')[0];
        el.src = base + '?width=1080';
        el.srcset = base + '?width=720 720w, ' + base + '?width=1080 1080w, ' + base + '?width=1440 1440w';
      });
      qsa('[data-up-variant-thumb]', scope).forEach(function (el) { el.src = img.split('?')[0] + '?width=88'; });
      var mediaId = v.featured_media ? v.featured_media.id : (v.featured_image ? v.featured_image.id : null);
      if (mediaId) qsa('[data-up-thumb]', scope).forEach(function (t) { var on = String(t.getAttribute('data-media-id')) === String(mediaId); t.classList.toggle('is-active', on); if (on && t.scrollIntoView && !initial) t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); });
    }
    /* PayPal messaging amount */
    qsa('[data-pp-message]', scope).forEach(function (el) { el.setAttribute('data-pp-amount', (v.price / 100).toFixed(2)); });
    /* url */
    if (this.context === 'pdp' && !initial && window.history && window.history.replaceState) {
      try { var u = new URL(window.location.href); u.searchParams.set('variant', v.id); window.history.replaceState({}, '', u.toString()); } catch (e) { /* ignore */ }
    }
    if (!initial) {
      var parts = [];
      var self = this;
      this.groups.forEach(function (g) { var gi = parseInt(g.getAttribute('data-up-option'), 10); var name = qs('.up-opt-name', g); parts.push((name ? name.textContent : '') + ': ' + mainText(v.options[gi])); });
      this._announce(parts.join(', ') + ' selected. ' + formatMoney(v.price) + (v.available ? (backorderFor(this.scope, v) ? '. Backordered, ships when restocked' : '') : '. ' + (geoLabelFor(this.scope, v) || 'Sold out')));
    }
  };
  UpVariantPicker.prototype._announce = function (text) { if (this.live) announce(text, this.live); };
  UpVariantPicker.prototype.getVariant = function () { return this.variant; };
  UpVariantPicker.prototype.setVariant = function (id) {
    var v = this.variants.filter(function (x) { return String(x.id) === String(id); })[0];
    if (!v) return;
    this._setInputs(v); this.variant = v; this._syncStates(); this._syncDom(false);
    this.root.dispatchEvent(new CustomEvent('up:variant', { bubbles: true, detail: { variant: v, product: this.productId, picker: this } }));
  };
  UpVariantPicker.prototype.on = function (evt, fn) { if (this.listeners[evt]) this.listeners[evt].push(fn); return this; };
  UpVariantPicker.prototype.setBusy = function (on) { this.groups.forEach(function (g) { g.setAttribute('aria-busy', on ? 'true' : 'false'); }); };
  window.UpVariantPicker = UpVariantPicker;
  function initPickers(root) {
    qsa('[data-up-picker]', root).forEach(function (el) { if (!el.__upPicker) new UpVariantPicker(el); });
  }

  /* --------------------------------------------- product form add to cart */
  function afterAdd(title) {
    if (setBool(S.openDrawerOnAdd, true)) { CloneCart.open(); }
    else { window.UpToast.show('Added to cart', { link: R.cart || '/cart', label: 'View cart' }); }
    announce('Item added to cart');
  }
  function showCartError(err) {
    var msg = (err && err.message) || 'Something went wrong. Please try again.';
    window.UpModal.open({ title: 'Please correct the following errors', html: '<ul><li>' + escapeHtml(msg) + '</li></ul>' });
  }
  function initProductForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-up-product-form]');
      if (!form) return;
      e.preventDefault();
      var btn = qs('[data-up-atc]', form);
      var text = btn ? qs('[data-up-atc-text]', btn) : null;
      var scope = form.closest('[data-up-product]') || document;
      var picker = qs('[data-up-picker]', scope);
      var fd = new FormData(form);
      var clean = new FormData();
      fd.forEach(function (val, key) { if (!/-(option-\d+|purchase-mode)$/.test(key)) clean.append(key, val); });
      if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
      if (picker && picker.__upPicker) picker.__upPicker.setBusy(true);
      qsa('[data-up-sticky-submit]', scope).forEach(function (b) { b.classList.add('is-loading'); });
      CloneCart.add(clean).then(function () {
        if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); btn.classList.add('is-added'); if (text) { var prev = text.textContent; text.textContent = S.addedLabel || 'Added'; setTimeout(function () { text.textContent = prev; btn.classList.remove('is-added'); }, 1200); } }
        afterAdd();
      }).catch(function (err) {
        if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
        showCartError(err);
      }).then(function () {
        if (picker && picker.__upPicker) picker.__upPicker.setBusy(false);
        qsa('[data-up-sticky-submit]', scope).forEach(function (b) { b.classList.remove('is-loading'); });
      });
    });
    /* quick add on cards (single variant) */
    document.addEventListener('click', function (e) {
      var q = e.target.closest('[data-up-quick-add]');
      if (!q) return;
      e.preventDefault();
      var id = q.getAttribute('data-up-quick-add');
      q.classList.add('is-loading');
      CloneCart.add({ items: [{ id: parseInt(id, 10), quantity: 1 }] }).then(function () { q.classList.remove('is-loading'); afterAdd(); })
        .catch(function (err) { q.classList.remove('is-loading'); showCartError(err); });
    });
  }

  /* ---------------------------------------------------------- sticky ATC */
  function initStickyAtc() {
    var bar = qs('[data-up-sticky-atc]');
    if (!bar) return;
    var scope = bar.closest('[data-up-product]') || document;
    var form = qs('form[data-up-product-form]', scope);
    var atc = qs('[data-up-atc]', form || scope);
    var submit = qs('[data-up-sticky-submit]', bar);
    if (!form || !atc) return;
    var desktopOk = bar.getAttribute('data-up-sticky-desktop') !== 'false';
    var mq = window.matchMedia('(min-width: 900px)');
    var passed = false;
    function apply() {
      var show = passed && !Drawers.isOpen() && (desktopOk || !mq.matches);
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (submit) submit.tabIndex = show ? 0 : -1;
      document.body.classList.toggle('up-has-sticky-atc', show && !mq.matches);
    }
    function measure() { var r = atc.getBoundingClientRect(); passed = r.bottom < 0; apply(); }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { passed = !en.isIntersecting && en.boundingClientRect.top < 0; });
        apply();
      }, { threshold: 0 }).observe(atc);
    }
    var ticking = false;
    window.addEventListener('scroll', function () { if (ticking) return; ticking = true; setTimeout(function () { ticking = false; measure(); }, 60); }, { passive: true });
    window.addEventListener('resize', debounce(measure, 100));
    document.addEventListener('up:drawer:open', apply);
    document.addEventListener('up:drawer:close', apply);
    document.addEventListener('up:modal:open', apply);
    document.addEventListener('up:modal:close', apply);
    if (mq.addEventListener) mq.addEventListener('change', apply);
    if (submit) submit.addEventListener('click', function (e) { e.preventDefault(); if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })); });
    measure();
  }

  /* ------------------------------------------------------- gallery thumbs */
  function initGallery() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-up-thumb]');
      if (t) {
        e.preventDefault();
        var scope = t.closest('[data-up-product]') || document;
        var src = t.getAttribute('data-src');
        qsa('[data-up-stage-img]', scope).forEach(function (img) { if (src) { img.src = src + '?width=1080'; img.srcset = src + '?width=720 720w, ' + src + '?width=1080 1080w, ' + src + '?width=1440 1440w'; } });
        qsa('[data-up-thumb]', scope).forEach(function (x) { x.classList.toggle('is-active', x === t); });
        return;
      }
      var stage = e.target.closest('[data-up-stage]');
      if (stage) {
        var img = qs('[data-up-stage-img]', stage);
        if (img && img.src) {
          var big = img.src.split('?')[0] + '?width=1440';
          window.UpModal.open({ borderless: true, html: '<img src="' + escapeHtml(big) + '" alt="' + escapeHtml(img.alt || '') + '">' });
        }
      }
    });
  }

  /* -------------------------------------------------------------- boot */
  ready(function () {
    initHeader();
    initAnnouncement();
    initNav();
    Drawers.init();
    window.UpModal.init();
    initCartLines();
    initQtySteppers();
    initSearch();
    initNewsletter();
    initPickers(document);
    initProductForms();
    initStickyAtc();
    initGallery();
    document.addEventListener('up:section:loaded', function (e) { initPickers(e.detail && e.detail.root ? e.detail.root : document); });
  });
})();
